import { useEffect, useState } from 'react';
import { getLastPaymentDate, getNumberOfPayments } from '@utils/billing';
import { ESchedulePaymentType, EFundsOperation } from '@enum';
import moment from 'moment-timezone';
import { useFormContext, useWatch } from 'react-hook-form';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { EScheduleFrequency } from '@xeppt/xeppt-sdk/types/schedule';
import { formatDate } from '@utils/index';
import { TTransactionMethodTypeAction } from '@xeppt/xeppt-sdk/types';
import { useMemo } from 'react';

interface IProps {
    isFrequencyExist?: boolean;
    method?: EFundsOperation;
}

export const requestMethods = [
    EFundsOperation.WALLET_DEBIT_REQUEST,
    EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
    EFundsOperation.EFT_DEBIT_DEPOSIT,
    EFundsOperation.EPS_DEBIT_DEPOSIT
];

export const useFrequency = ({ isFrequencyExist, method }: IProps) => {
    const form = useFormContext();
    const { account, limits, loyalty } = useUserContext();
    const { validationLocale } = useLocales();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const watchedFrequency = form?.control
        ? useWatch({ name: 'paymentFrequency', control: form.control })
        : null;
    const watchedAmount = form?.control
        ? useWatch({ name: 'amount', control: form.control })
        : null;
    const watchedNextPaymentAt = form?.control
        ? useWatch({ name: 'next_payment_at', control: form.control })
        : null;
    const watchedNumberOfPayments = form?.control
        ? useWatch({ name: 'number_of_payments', control: form.control })
        : null;
    const watchedPaymentsEndDate = form?.control
        ? useWatch({
              name: 'paymentsEndDate',
              control: form.control
          })
        : null;
    const watchedPaymentType = form?.control
        ? useWatch({ name: 'paymentType', control: form.control })
        : null;
    const isSchedule = form?.control
        ? useWatch({ name: 'isSchedule', control: form.control })
        : null;
    const giftCardsCount = form?.control
        ? useWatch({ name: 'giftCardCount', control: form.control })
        : null;

    const isBulkGiftCards = form?.control
        ? useWatch({ name: 'isBulkGiftCards', control: form.control })
        : null;

    useEffect(() => {
        if (isFrequencyExist) {
            const resultNumberOfPayments = getNumberOfPayments({
                frequency: watchedFrequency,
                nextPaymentDate: watchedNextPaymentAt,
                lastPaymentDate: watchedPaymentsEndDate
            });
            if (
                watchedNumberOfPayments !== resultNumberOfPayments &&
                watchedPaymentType === ESchedulePaymentType.PAYMENTS_DATE
            ) {
                form.setValue(
                    'number_of_payments',
                    resultNumberOfPayments > 0 ? resultNumberOfPayments : 1
                );
            }
        }
    }, [watchedPaymentsEndDate, watchedFrequency, isFrequencyExist, watchedNextPaymentAt]);

    useEffect(() => {
        if (isFrequencyExist) {
            const resultLastPaymentDate = getLastPaymentDate({
                frequency: watchedFrequency,
                nextPaymentAt: watchedNextPaymentAt || new Date(),
                donePayments: 0,
                number_of_payments: watchedNumberOfPayments
            });
            if (
                formatDate(watchedPaymentsEndDate, 'YYYY-MM-DD') !==
                    formatDate(resultLastPaymentDate, 'YYYY-MM-DD') &&
                watchedPaymentType === ESchedulePaymentType.NUMBER_OF_PAYMENTS
            ) {
                form.setValue('paymentsEndDate', resultLastPaymentDate);
            }
        }
    }, [watchedNumberOfPayments, watchedFrequency, isFrequencyExist, watchedNextPaymentAt]);

    useEffect(() => {
        if (isFrequencyExist) {
            if (!isFirstRender) {
                watchedAmount && Number(watchedAmount) !== 0 && form.trigger('amount');
            } else {
                setIsFirstRender(false);
            }
        }
    }, [watchedNextPaymentAt, watchedFrequency, isFrequencyExist]);

    useEffect(() => {
        if (isFrequencyExist) {
            const resultLastPaymentDate = getLastPaymentDate({
                frequency: watchedFrequency,
                nextPaymentAt: watchedNextPaymentAt,
                donePayments: 0,
                number_of_payments: watchedNumberOfPayments
            });
            const resultNumberOfPayments = getNumberOfPayments({
                frequency: watchedFrequency,
                nextPaymentDate: watchedNextPaymentAt,
                lastPaymentDate: resultLastPaymentDate
            });
            form.setValue('paymentsEndDate', resultLastPaymentDate);
            form.setValue(
                'number_of_payments',
                resultNumberOfPayments > 0 ? resultNumberOfPayments : 1
            );
        }
    }, [watchedNextPaymentAt]);

    const getLimits = (type: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>) => {
        // Payment-popup purchase ops (WALLET_*_PAYMENT) are not keys of the
        // SDK limits map and are never passed here at runtime (purchases aren't
        // in the send/request method lists); cast to satisfy the index type.
        return limits?.[type as TTransactionMethodTypeAction];
    };

    const getMinLimitsValidation = (
        amount: number,
        type: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
    ) => {
        const methodLimits = getLimits(type);
        if (amount < (methodLimits?.amount_transfer_min || 0)) {
            return validationLocale('must_be_more_limit').replace(
                '{{amount}}',
                (methodLimits?.amount_transfer_min || 0).toFixed(2)
            );
        }
    };

    const getLimitsValidationByMethod = (
        amount: number,
        type: Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
    ) => {
        const methodLimits = getLimits(type);
        console.log({ amount, methodLimits });
        if (
            methodLimits?.daily !== 0 &&
            amount > (methodLimits?.daily || 0) - (methodLimits?.daily_used || 0)
        ) {
            return validationLocale('not_enough_daily_limit');
        } else if (
            methodLimits?.weekly !== 0 &&
            amount > (methodLimits?.weekly || 0) - (methodLimits?.weekly_used || 0)
        ) {
            return validationLocale('not_enough_weekly_limit');
        } else if (
            methodLimits?.monthly !== 0 &&
            amount > (methodLimits?.monthly || 0) - (methodLimits?.monthly_used || 0)
        ) {
            return validationLocale('not_enough_monthly_limit');
        } else if (methodLimits?.per_transfer !== 0 && amount > (methodLimits?.per_transfer || 0)) {
            return validationLocale('not_enough_transaction_limit');
        }
    };

    const amountResultValidation = (stringAmount: string) => {
        const amount = Number(stringAmount);
        if (
            !isSchedule &&
            amount > (account?.balance || 0) &&
            !requestMethods?.includes(method as EFundsOperation)
        ) {
            return validationLocale('not_enough_balance');
        }
        if (amount <= 0) {
            return 'Amount must be greater than 0';
        }
    };

    const validateGiftCardAmount = (stringAmount: string) => {
        const amount = Number(stringAmount);
        const issueFlat =
            loyalty?.current_plan?.[EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD]?.flat || 0;
        const totalAmount = issueFlat * (isBulkGiftCards ? Number(giftCardsCount) : 1);
        if (totalAmount > (account?.balance || 0) || amount > (account?.balance || 0)) {
            return validationLocale('not_enough_balance');
        }
        return (
            getMinLimitsValidation(
                amount,
                method as Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
            ) ||
            getLimitsValidationByMethod(
                amount,
                method as Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
            )
        );
    };

    const giftCardFeePoints = useMemo(() => {
        // Gift-card issue fee + points come from loyalty plan key 8_1_13 — the single
        // source the backend charge uses. Fee is FLAT-ONLY (percent ignored for issue);
        // points are the plan's per-card points. Replaces the retired OrderGiftCard product.
        const plan = loyalty?.current_plan?.[EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD];
        const flat = plan?.flat || 0;
        const points = plan?.points || 0;
        return {
            fee: isBulkGiftCards ? flat * giftCardsCount : flat,
            points: isBulkGiftCards ? points * giftCardsCount : points
        };
    }, [method, giftCardsCount, isBulkGiftCards, loyalty]);

    const amountValidation = (stringAmount: string) => {
        const amount = Number(stringAmount);
        if (amount <= 0) {
            return 'Amount must be greater than 0';
        } else {
            if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
                return validateGiftCardAmount(stringAmount);
            } else if (
                watchedFrequency === EScheduleFrequency.ONCE ||
                moment(watchedNextPaymentAt).isSame(moment(), 'day') ||
                !isFrequencyExist
            ) {
                if (
                    !isSchedule &&
                    amount > (account?.balance || 0) &&
                    !requestMethods?.includes(
                        method as Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
                    )
                ) {
                    return validationLocale('not_enough_balance');
                } else if (!!method) {
                    return (
                        getMinLimitsValidation(
                            amount,
                            method as Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
                        ) ||
                        getLimitsValidationByMethod(
                            amount,
                            method as Exclude<EFundsOperation, EFundsOperation.CARD_DEBIT_DEPOSIT>
                        )
                    );
                }
            }
        }
    };

    return {
        amountValidation,
        amountResultValidation,
        giftCardFeePoints
    };
};
